import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatCard } from '../components/StatCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { IcReceipt, IcTruck, IcDollar, IcAlert, IcClock } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import {
  getFedexInvoice,
  reconcileFedexInvoice,
  deleteFedexInvoice,
  ApiError,
  type FedexBrandRow,
  type FedexChargeRow,
} from '../lib/api';
import { fmtCents, fmtNum, fmtDate, fmtUtcDate } from '../lib/format';
import {
  DEFAULT_FEDEX_SHIPMENT_COLUMN_KEYS,
  FEDEX_SHIPMENT_COLUMN_KEYS,
  FEDEX_SHIPMENT_COLUMN_PREFERENCES_STORAGE_KEY,
  parseFedexShipmentColumnPreferences,
  serializeFedexShipmentColumnPreferences,
  type FedexShipmentColumnKey,
} from '../lib/fedexShipmentColumns';

const COLUMN_LABELS: Record<FedexShipmentColumnKey, string> = {
  tracking: 'Tracking',
  shipDate: 'Ship date',
  service: 'Service',
  sla: 'SLA',
  packageType: 'Package',
  brand: 'Brand',
  zone: 'Zone',
  transportation: 'Transportation',
  discount: 'Discount',
  realCost: 'Real cost',
  charged: 'Charged',
  margin: 'Margin',
  flags: 'Flags',
};

const RIGHT_ALIGNED_COLUMNS = new Set<FedexShipmentColumnKey>([
  'transportation',
  'discount',
  'realCost',
  'charged',
  'margin',
]);

const FLAG_LABELS: Record<string, string> = {
  unmatched: 'Unmatched (not in pharmacy)',
  unmapped_brand: 'Brand not a billing client',
  unmapped_service: 'Service not in rate card',
  no_rate: 'No rate configured',
  negative_margin: 'Cost exceeds what we charged',
};

export const FedexBillDetailPage = () => {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const reupload = (loc.state as { reupload?: boolean } | null)?.reupload === true;
  const { can } = useAuth();
  const detail = useFetch(() => getFedexInvoice(id), [id]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<FedexShipmentColumnKey[]>(
    loadVisibleColumns,
  );
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [lateOnly, setLateOnly] = useState(false);
  const columnsRef = useRef<HTMLDivElement>(null);
  const columnsButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        FEDEX_SHIPMENT_COLUMN_PREFERENCES_STORAGE_KEY,
        serializeFedexShipmentColumnPreferences(visibleColumns),
      );
    } catch {
      // Browser storage can be unavailable; column selection still works for
      // the current page session.
    }
  }, [visibleColumns]);

  useEffect(() => {
    if (!columnsOpen) return;

    const closeOutside = (event: MouseEvent) => {
      if (!columnsRef.current?.contains(event.target as Node)) setColumnsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setColumnsOpen(false);
      columnsButtonRef.current?.focus();
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [columnsOpen]);

  function toggleColumn(key: FedexShipmentColumnKey) {
    if (key === 'tracking') return;
    setVisibleColumns((current) =>
      current.includes(key)
        ? current.filter((candidate) => candidate !== key)
        : FEDEX_SHIPMENT_COLUMN_KEYS.filter(
            (candidate) => candidate === key || current.includes(candidate),
          ),
    );
  }

  async function reconcile() {
    setBusy(true);
    setErr(null);
    try {
      await reconcileFedexInvoice(id);
      detail.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Reconcile failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Delete this FedEx bill and all its parsed charges?')) return;
    setBusy(true);
    try {
      await deleteFedexInvoice(id);
      nav('/fedex');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Delete failed');
      setBusy(false);
    }
  }

  return (
    <Page>
      <Async state={detail}>
        {({ invoice, byBrand, flagCounts, slaCounts, charges }) => {
          const displayedCharges = lateOnly
            ? charges.filter((charge) => charge.sla.status === 'late')
            : charges;
          const hasPartialShipmentSet = charges.length < invoice.shipmentCount;

          return (
            <>
            <PageHeader
              title={`FedEx ${invoice.fedexInvoiceNumber ?? invoice.fileName}`}
              sub={`${invoice.invoiceDate ? fmtUtcDate(invoice.invoiceDate) : 'Undated'} · ${fmtNum(invoice.shipmentCount)} shipments · ${fmtNum(invoice.matchedCount)} matched, ${fmtNum(invoice.unmatchedCount)} unmatched`}
              actions={
                can('billing:run') ? (
                  <>
                    <button className="btn btn--ghost" onClick={reconcile} disabled={busy}>
                      {busy ? 'Working…' : 'Re-reconcile'}
                    </button>
                    <button className="btn btn--ghost" onClick={remove} disabled={busy}>
                      Delete
                    </button>
                  </>
                ) : (
                  <Link className="btn btn--ghost" to="/fedex">
                    Back
                  </Link>
                )
              }
            />

            {reupload && (
              <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, background: 'rgba(251,191,36,.12)', color: 'var(--status-attention-ink,#92400e)' }}>
                This bill was already uploaded{invoice.createdAt ? ` on ${fmtDate(invoice.createdAt)}` : ''}
                {invoice.uploadedByEmail ? ` by ${invoice.uploadedByEmail}` : ''} — showing the existing record (not re-imported).
              </div>
            )}

            {err && (
              <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)' }}>
                {err}
              </div>
            )}

            <div className="grid grid-6">
              <StatCard tone="in-transit" icon={IcTruck} label="Express total (shipments)" value={fmtCents(invoice.expressTotalCents)} sub={`${fmtNum(invoice.shipmentCount)} shipments`} />
              <StatCard tone="staged" icon={IcReceipt} label="Other charges" value={fmtCents(invoice.otherChargesCents)} sub="late fees / adjustments" />
              <StatCard tone="success" icon={IcDollar} label="Grand total" value={fmtCents(invoice.grandTotalCents)} sub="TOTAL THIS INVOICE" />
              <StatCard tone={invoice.unmatchedCount ? 'attention' : 'success'} icon={IcAlert} label="Unmatched" value={fmtNum(invoice.unmatchedCount)} sub={`of ${fmtNum(invoice.shipmentCount)} shipments`} />
              <StatCard
                tone={slaCounts.late ? 'error' : 'success'}
                icon={IcClock}
                label={hasPartialShipmentSet ? 'SLA late (loaded)' : 'SLA late'}
                value={fmtNum(slaCounts.late)}
                sub={`${fmtNum(slaCounts.onTime)} on time`}
              />
              <StatCard
                tone={slaCounts.unknown ? 'attention' : 'success'}
                icon={IcAlert}
                label={hasPartialShipmentSet ? 'SLA unknown (loaded)' : 'SLA unknown'}
                value={fmtNum(slaCounts.unknown)}
                sub="missing or unsupported evidence"
              />
            </div>

            {invoice.warnings.length > 0 && (
              <PCard style={{ padding: 12, fontSize: 13, color: 'var(--p-muted-fg)' }}>
                {invoice.warnings.map((w, i) => (
                  <div key={i}>⚠︎ {w}</div>
                ))}
              </PCard>
            )}

            {flagCounts.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {flagCounts.map((f) => (
                  <span
                    key={f.flag}
                    style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, background: 'var(--p-muted-bg,rgba(0,0,0,.05))', color: 'var(--p-muted-fg)' }}
                  >
                    {FLAG_LABELS[f.flag] ?? f.flag}: <strong>{fmtNum(f.count)}</strong>
                  </span>
                ))}
              </div>
            )}

            <div>
              <div className="section-title">Real cost by brand</div>
              <PCard>
                <table className="p-table" aria-label="Real cost by brand">
                  <thead>
                    <tr>
                      <th>Brand (clinic)</th>
                      <th>Billing client</th>
                      <th style={{ textAlign: 'right' }}>Shipments</th>
                      <th style={{ textAlign: 'right' }}>Real cost (FedEx)</th>
                      <th style={{ textAlign: 'right' }}>We charged</th>
                      <th style={{ textAlign: 'right' }}>Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byBrand.map((b, i) => (
                      <BrandRow key={i} b={b} />
                    ))}
                  </tbody>
                </table>
              </PCard>
            </div>

            <div>
              <div className="fedex-table-toolbar">
                <div
                  id="fedex-shipments-title"
                  className="section-title"
                  style={{ margin: 0 }}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  Shipments ({fmtNum(displayedCharges.length)}{lateOnly ? ` of ${fmtNum(charges.length)}` : ''})
                </div>
                <div className="fedex-table-toolbar__controls">
                  <label className="fedex-late-filter">
                    <input
                      type="checkbox"
                      checked={lateOnly}
                      onChange={(event) => setLateOnly(event.target.checked)}
                    />
                    <span>{hasPartialShipmentSet ? 'Late only (loaded)' : 'Late only'}</span>
                    <strong className="tabular">{fmtNum(slaCounts.late)}</strong>
                  </label>
                  <div className="fedex-column-picker" ref={columnsRef}>
                    <button
                      ref={columnsButtonRef}
                      type="button"
                      className="btn btn--ghost"
                      aria-expanded={columnsOpen}
                      aria-controls="fedex-column-options"
                      onClick={() => setColumnsOpen((open) => !open)}
                    >
                      Columns
                    </button>
                    {columnsOpen && (
                      <div id="fedex-column-options" className="fedex-column-picker__popover">
                        <fieldset>
                          <legend>Show columns</legend>
                          {FEDEX_SHIPMENT_COLUMN_KEYS.map((key) => (
                            <label key={key}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes(key)}
                                disabled={key === 'tracking'}
                                onChange={() => toggleColumn(key)}
                              />
                              <span>{COLUMN_LABELS[key]}</span>
                              {key === 'tracking' && <small>Required</small>}
                            </label>
                          ))}
                        </fieldset>
                        <button
                          type="button"
                          className="fedex-column-picker__reset"
                          onClick={() => setVisibleColumns([...DEFAULT_FEDEX_SHIPMENT_COLUMN_KEYS])}
                        >
                          Reset defaults
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {hasPartialShipmentSet && (
                <div className="fedex-partial-note" role="note">
                  Showing {fmtNum(charges.length)} of {fmtNum(invoice.shipmentCount)} shipments. SLA
                  counts and the Late only filter apply to the loaded rows.
                </div>
              )}
              <PCard>
                <div style={{ overflowX: 'auto' }}>
                  <table className="p-table" aria-labelledby="fedex-shipments-title">
                    <thead>
                      <tr>
                        {visibleColumns.map((key) => (
                          <th
                            key={key}
                            style={RIGHT_ALIGNED_COLUMNS.has(key) ? { textAlign: 'right' } : undefined}
                          >
                            {COLUMN_LABELS[key]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayedCharges.map((c) => (
                        <ChargeRow
                          key={c._id}
                          c={c}
                          invoiceId={invoice.id}
                          visibleColumns={visibleColumns}
                          onOpen={() => nav(`/fedex/${invoice.id}/shipments/${c._id}`)}
                        />
                      ))}
                      {displayedCharges.length === 0 && (
                        <tr>
                          <td className="fedex-empty-row" colSpan={visibleColumns.length}>
                            <span role="status">
                              {lateOnly ? 'No late shipments in this bill.' : 'No shipments in this bill.'}
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PCard>
            </div>
            </>
          );
        }}
      </Async>
    </Page>
  );
};

function Margin({ cents }: { cents?: number }) {
  if (cents === undefined) return <span className="muted">—</span>;
  return <span style={{ color: cents < 0 ? 'var(--status-error-ink,#b91c1c)' : 'var(--status-success-ink,#047857)' }}>{fmtCents(cents)}</span>;
}

function BrandRow({ b }: { b: FedexBrandRow }) {
  const rated = b.ratedShipments > 0;
  return (
    <tr>
      <td style={{ fontWeight: 500 }}>{b.clinic ?? <span className="muted">Unmatched</span>}</td>
      <td>{b.clientName ?? <span className="muted">{b.clinic ? 'not a billing client' : '—'}</span>}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{fmtNum(b.shipments)}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(b.realCostCents)}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{rated ? fmtCents(b.chargedCents) : <span className="muted">—</span>}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{rated ? <Margin cents={b.marginCents} /> : <span className="muted">—</span>}</td>
    </tr>
  );
}

function ChargeRow({
  c,
  invoiceId,
  visibleColumns,
  onOpen,
}: {
  c: FedexChargeRow;
  invoiceId: string;
  visibleColumns: readonly FedexShipmentColumnKey[];
  onOpen: () => void;
}) {
  const detailPath = `/fedex/${invoiceId}/shipments/${c._id}`;
  const openFromRow = (event: ReactMouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('a,button,input,label')) return;
    onOpen();
  };

  return (
    <tr className="fedex-shipment-row" onClick={openFromRow}>
      {visibleColumns.map((key) => (
        <ChargeCell key={key} column={key} charge={c} detailPath={detailPath} />
      ))}
    </tr>
  );
}

function ChargeCell({
  column,
  charge,
  detailPath,
}: {
  column: FedexShipmentColumnKey;
  charge: FedexChargeRow;
  detailPath: string;
}) {
  switch (column) {
    case 'tracking':
      return (
        <td className="tabular">
          <Link
            className="fedex-shipment-link"
            to={detailPath}
            onClick={(event) => event.stopPropagation()}
            aria-label={`View shipment ${charge.trackingNumber ?? charge._id}`}
          >
            {charge.trackingNumber ?? `…${charge._id.slice(-8)}`}
          </Link>
          {charge.matched && charge.matchStrategy === 'fill_id' && (
            <span className="muted" style={{ fontSize: 11 }}> (via ref)</span>
          )}
        </td>
      );
    case 'shipDate':
      return <td>{charge.shipDate ? fmtUtcDate(charge.shipDate) : '—'}</td>;
    case 'service':
      return <td>{charge.serviceTypeText ?? <span className="muted">—</span>}</td>;
    case 'sla':
      return (
        <td>
          <SlaStatusPill charge={charge} />
        </td>
      );
    case 'packageType':
      return (
        <td>
          {charge.packageType ? (
            <span className="fedex-package-value">{charge.packageType}</span>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
      );
    case 'brand':
      return (
        <td>
          {charge.clinic ?? <span className="muted">—</span>}
          {charge.matched && charge.matchSource === 'v2' && (
            <span className="muted" style={{ fontSize: 11 }}> · V2</span>
          )}
        </td>
      );
    case 'zone':
      return <td>{charge.zone ?? <span className="muted">—</span>}</td>;
    case 'transportation':
      return <MoneyCell cents={charge.transportationCents} />;
    case 'discount':
      return <MoneyCell cents={charge.discountCents} />;
    case 'realCost':
      return <MoneyCell cents={charge.realCostCents} />;
    case 'charged':
      return <MoneyCell cents={charge.chargedCents} />;
    case 'margin':
      return (
        <td className="tabular" style={{ textAlign: 'right' }}>
          <Margin cents={charge.marginCents} />
        </td>
      );
    case 'flags':
      return (
        <td className="fedex-flags-cell">
          {charge.billed && <StatusPill tone="success">billed</StatusPill>}{' '}
          {charge.flags
            .filter((flag) => flag !== 'unmatched' || !charge.matched)
            .map((flag) => (
              <StatusPill
                key={flag}
                tone={flag === 'negative_margin' || flag === 'unmatched' ? 'error' : 'attention'}
              >
                {flag.replace(/_/g, ' ')}
              </StatusPill>
            ))}
        </td>
      );
  }
}

function SlaStatusPill({ charge }: { charge: FedexChargeRow }) {
  if (charge.sla.status === 'late') {
    const days = charge.sla.businessDaysLate;
    return (
      <StatusPill tone="error">
        {days === null
          ? 'Late'
          : days === 0
            ? 'Late · under 1 business day'
            : `${days} business ${days === 1 ? 'day' : 'days'} late`}
      </StatusPill>
    );
  }
  if (charge.sla.status === 'on_time') return <StatusPill tone="success">On time</StatusPill>;
  return <StatusPill tone="neutral">Unknown</StatusPill>;
}

function MoneyCell({ cents }: { cents?: number }) {
  return (
    <td className="tabular" style={{ textAlign: 'right' }}>
      {cents === undefined ? <span className="muted">—</span> : fmtCents(cents)}
    </td>
  );
}

function loadVisibleColumns(): FedexShipmentColumnKey[] {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(FEDEX_SHIPMENT_COLUMN_PREFERENCES_STORAGE_KEY);
  } catch {
    // Ignore browser storage failures and use defaults.
  }
  const parsed = parseFedexShipmentColumnPreferences(stored);
  if (parsed.includes('tracking')) return parsed;
  return FEDEX_SHIPMENT_COLUMN_KEYS.filter(
    (key) => key === 'tracking' || parsed.includes(key),
  );
}
